import clsx, { type ClassValue } from 'clsx';
import { useEffect, useState } from 'react';

export function cn(...values: ClassValue[]): string {
  return clsx(values);
}

/**
 * Identity colors, in the order they are handed out. Validated as a set for
 * color-blind separation between *adjacent* slots — see `styles/theme.css`.
 * `slate` is the neutral for anything unassigned and is not part of the eight.
 */
export const ACCENT_TOKENS = [
  'sky',
  'amber',
  'teal',
  'plum',
  'rose',
  'sand',
  'indigo',
  'clay',
] as const;

export type AccentToken = (typeof ACCENT_TOKENS)[number] | 'slate';

export function accentVar(token: string | null | undefined): string {
  const safe =
    token && [...ACCENT_TOKENS, 'slate'].includes(token as AccentToken) ? token : 'slate';
  return `var(--c-${safe})`;
}

/** Series color for chart slot `index`; past the eighth, everything is "other". */
export function seriesColor(index: number): string {
  return accentVar(ACCENT_TOKENS[index] ?? 'slate');
}

export function initials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  // CJK names read best as the given name's first character rather than a
  // two-letter Latin-style monogram.
  if (/[一-鿿]/.test(trimmed)) return trimmed.slice(-1);
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 1).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );

  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export const useIsDesktop = () => useMediaQuery('(min-width: 900px)');

/** Debounces a fast-changing value — search boxes, mostly. */
export function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Locks background scrolling while a sheet or dialog is open. */
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [active]);
}

/**
 * Parses what a person types into an amount box into integer cents. Mirrors the
 * server's parser closely enough for live feedback; the server is still the
 * authority on what gets stored.
 */
export function parseAmountInput(input: string): number | null {
  const cleaned = input.replace(/[^\d.,-]/g, '').replace(/,/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.') return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value)) return null;
  return Math.round(value * 100);
}

export function centsToInput(cents: number): string {
  if (!cents) return '';
  return (cents / 100).toFixed(2);
}

/** Rough password strength, used only to reassure — never to block. */
export function passwordStrength(password: string): 0 | 1 | 2 | 3 {
  if (password.length < 10) return 0;
  let score = 0;
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;
  if (/\s/.test(password) && password.trim().split(/\s+/).length >= 3) score += 1;
  if (/[^A-Za-z0-9]/.test(password) && /\d/.test(password)) score += 1;
  return Math.min(score, 3) as 0 | 1 | 2 | 3;
}
